import React, { useEffect } from 'react';
import useMatchMedia from '@dashboard/hooks/useMatchMedia';

const PreviewStateContext = React.createContext();
const PreviewDispatchContext = React.createContext();

function previewReducer(state, action) {
  switch (action.type) {
    case 'toggle': {
      return {
        ...state,
        open: !state.open,
      };
    }
    case 'open': {
      return {
        ...state,
        open: true,
      };
    }
    case 'close': {
      return {
        ...state,
        open: false,
      };
    }
    case 'set': {
      return {
        ...state,
        ...action.value,
      };
    }
    case 'setDeviceSize': {
      return {
        ...state,
        deviceSize: action.value,
      };
    }
    case 'setColorMode': {
      return {
        ...state,
        colorMode: action.value,
      };
    }
    case 'setFullscreen': {
      return {
        ...state,
        isFullscreen: action.value,
      };
    }
    case 'setViewType': {
      return {
        ...state,
        type: action.value,
      };
    }
    // The AI's screenshot needs the page itself at its device; the seller's view comes back after.
    case 'startCapture': {
      return {
        ...state,
        beforeCapture: state.beforeCapture ?? { deviceSize: state.deviceSize, type: state.type },
        deviceSize: action.value,
        type: 'checkout',
      };
    }
    case 'endCapture': {
      if (!state.beforeCapture) return state;
      const { beforeCapture, ...rest } = state;
      return { ...rest, ...beforeCapture };
    }
    case 'setUpsellPageId': {
      return {
        ...state,
        upsellPageId: action.value,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function getVariantFromPage(page) {
  if (page.usageType === 'hosted') {
    return 'hosted_page';
  }

  if (page.usageType === 'site') {
    if (page.siteLayoutType === 'embed') {
      return 'site_embed';
    }

    if (page.siteLayoutType === 'popup') {
      return 'site_popup';
    }
  }

  return 'page';
}

/**
 * Returns the preview color mode that matches a theme's color scheme setting.
 * For 'system', uses the OS preference via matchMedia.
 */
function getColorModeForScheme(colorScheme) {
  if (colorScheme === 'dark') return 'dark';
  if (colorScheme === 'system') {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

function PreviewProvider({ page, children }) {
  const isSmall = useMatchMedia('(max-width: 768px)');

  const [state, dispatch] = React.useReducer(previewReducer, null, () => {
    const initialColorScheme =
      page?.themeOverrides?.variables?.tokens?.colors ?? page?.theme?.variables?.tokens?.colors;
    return {
      open: isSmall ? false : true,
      type: 'checkout',
      variant: getVariantFromPage(page),
      deviceSize: 'desktop',
      colorMode: getColorModeForScheme(initialColorScheme),
      isFullscreen: false,
      upsellPageId: null,
      previewPriceId: null,
    };
  });

  useEffect(() => {
    dispatch({ type: 'set', value: { variant: getVariantFromPage(page) } });
  }, [page.usageType, page.siteLayoutType, page.hostedLayoutType]);

  return (
    <PreviewStateContext.Provider value={state}>
      <PreviewDispatchContext.Provider value={dispatch}>{children}</PreviewDispatchContext.Provider>
    </PreviewStateContext.Provider>
  );
}

function usePreviewState() {
  const context = React.useContext(PreviewStateContext);

  if (context === undefined) {
    throw new Error('usePreviewState must be used within a PreviewProvider');
  }

  return context;
}

function usePreviewDispatch() {
  const context = React.useContext(PreviewDispatchContext);

  if (context === undefined) {
    throw new Error('usePreviewDispatch must be used within a PreviewProvider');
  }

  return context;
}

function usePreview() {
  return [usePreviewState(), usePreviewDispatch()];
}

export { PreviewProvider, usePreview, getColorModeForScheme, previewReducer };
